import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import { useMemo } from 'react'
import { useMutation } from 'Data/Api.js'
import { useDataChange, useDataSubmit, useDataValue } from 'Simple/Data'
import { normalizePath, useSetFlowTo } from 'Simple/Flow'
import mutation from './mutation.graphql.js'

export function useOnRowClick(props) {
  let change = useDataChange({
    context: 'selected',
    viewPath: props.viewPath,
  })

  let setFlowTo = useSetFlowTo(props.viewPath)

  return function onRowClick(row) {
    change(next => {
      next.question_id = row.original.id
      next.question_type_id = null
    })
    setFlowTo(normalizePath(props.viewPath, './Editor/Content'))
  }
}

export function useOnReorder(props) {
  let questions = useDataValue({
    context: 'questions',
    viewPath: props.viewPath,
  })

  let configuration_id = useDataValue({
    context: 'selected',
    path: 'configuration_id',
    viewPath: props.viewPath,
  })
  let submit = useDataSubmit({
    context: 'questions',
    viewPath: props.viewPath,
  })

  let [, notify] = useNotifications()

  let [, executeMutation] = useMutation(mutation)

  return async function onReorder(data) {
    let differences = getOrderDifferences(
      data.map(question => ({
        id: question.id,
        order: question.order,
      })),
      questions.map(question => ({
        id: question.id,
        order: question.order,
      }))
    )

    if (!differences) return false

    let validDependancies = differences.map(difference => {
      let question = data.find(item => item.id === difference.id)

      return areDependenciesValid({
        depends_on: question.depends_on,
        question_order: difference.newOrder,
        questions: data,
      })
    })

    if (validDependancies.includes(false)) {
      notify(
        notifyError(
          'Reordering failed, check the dependancies order and try again'
        )
      )
      return false // give back the original order = reset
    }

    let differenceChangeById = Object.fromEntries(
      differences.map(item => [item.id, item.newOrder])
    )

    let mutationResponse = await executeMutation({
      updates: Object.entries(differenceChangeById).map(([id, order]) => ({
        where: {
          id: { _eq: id },
          configuration_id: { _eq: configuration_id },
        },
        _set: { order },
      })),
    })

    if (mutationResponse.error) {
      notify(notifyError('Reordering failed, try again'))
    } else {
      notify(notifySuccess('Questions Reordered'))
    }

    await submit({ type: 'reFetch' })

    return true
  }
}

function areDependenciesValid({ depends_on, question_order, questions }) {
  return (
    depends_on === null ||
    depends_on === undefined ||
    (Array.isArray(depends_on) &&
      depends_on.every(depend => {
        let dependency_order =
          questions.find(question =>
            [question.id, question.resource_id].includes(depend.id)
          )?.order || -1

        return (
          !!depend.id &&
          !!depend.validation &&
          dependency_order < question_order &&
          depend.value !== undefined &&
          depend.value !== null &&
          !!depend.value.toString().trim().length
        )
      }))
  )
}

function getOrderDifferences(newList, oldList) {
  // Create lookup maps for old and new
  let oldMap = Object.fromEntries(oldList.map(item => [item.id, item.order]))
  let newMap = Object.fromEntries(newList.map(item => [item.id, item.order]))

  // Compare orders
  let differences = []

  for (let id in newMap) {
    if (newMap[id] !== oldMap[id]) {
      differences.push({
        id,
        oldOrder: oldMap[id],
        newOrder: newMap[id],
      })
    }
  }

  return differences
}

function dependsOnFormat({ questions, depends_on = [], prompt }) {
  if (!depends_on?.length) return prompt

  let orderList = depends_on
    .map(
      item =>
        questions.find(q => q.id === item.id || q.resource_id === item.id)
          ?.order
    )
    .filter(order => order !== undefined)

  return (
    <>
      <em style={{ marginRight: 4, color: '#3e4c59' }}>
        ↳ (depends on item {orderList.join(', ')})
      </em>
      {prompt}
    </>
  )
}

export function useTableRowsData(questions) {
  let columns = [
    {
      header: 'Order',
      accessorKey: 'order',
      cell: cell => cell.getValue() ?? '-',
    },
    {
      header: 'Prompt',
      accessorKey: 'prompt',
      cell: cell => {
        return dependsOnFormat({
          questions,
          depends_on: cell.row.original?.depends_on,
          prompt:
            !cell.getValue() || cell.getValue() === '' ? '-' : cell.getValue(),
        })
      },
    },
    {
      header: 'Required',
      accessorKey: 'required',
      cell: cell => (cell.getValue() ? 'Yes' : 'No'),
    },
  ]

  return useMemo(
    () => ({
      columns,
      data: questions ?? [],
    }),
    [questions]
  )
}
